// Import children components
import './btn-import-recipe';
import './requirements';

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-template-sidebar' )
export class AutomatorTemplateSidebar extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * The ID of the template.
	 *
	 * @type {Number}
	 * @property {Number} templateID - Represents the unique ID of the template.
	 * Attribute name in HTML: `template-id`.
	 */
	@property( { type: Number, attribute: 'template-id' } )
	accessor templateID;

	/**
	 * The integrations required by the recipe.
	 * Each requirement is represented as an object with relevant details.
	 *
	 * @type {Array<Object>}
	 * @property {Array<Object>} requirements - An array of required integrations for the recipe.
	 * Attribute name in HTML: none (non-reflected property).
	 * Default: `[]`.
	 */
	@property( { type: Array, attribute: false } )
	accessor requirements = [];

	/**
	 * A flag indicating if the template operates in standalone mode, typically used when the template library is embedded on an external site. Disables installation-related functionality and hides import buttons. Default is false.
	 *
	 * @type {Boolean} The standalone mode flag
	 */
	@property( { type: Boolean, attribute: 'standalone-mode' } )
	accessor standaloneMode = false;

	// eslint-disable-next-line require-jsdoc
	render() {
		// Get data or show placeholder
		return html`
			<div class="sidebar">
				${ this._templateHeader }
				${ this._templateContent }
				${ this._templateCTA }
			</div>
		`;
	}

	/**
	 * Requests the dialog to close itself
	 */
	requestCloseDialog() {
		this.dispatchEvent(
			new CustomEvent(
				'uap-template-request-dialog-close',
				{
					bubbles: true,
					composed: true
				}
			)
		);

		return;
	}

	/**
	 * Returns the template of the header of the sidebar
	 *
	 * @return {TemplateResult} The template of the header
	 */
	get _templateHeader() {
		// Check if the template is in standalone mode
		if ( this.standaloneMode ) {
			return html`
				<div class="sidebar-header">
					<div class="sidebar-header-title">
						${ escapeHTML( __( 'Recipe details', 'uncanny-automator' ) ) }
					</div>
				</div>
			`;
		}

		return html`
			<div class="sidebar-header">
				<div class="sidebar-header-title">
					${ escapeHTML( __( 'Recipe details', 'uncanny-automator' ) ) }
				</div>

				<uo-tooltip
					class="sidebar-header-close-dialog"
				>
					${ escapeHTML( __( 'Close template', 'uncanny-automator' ) ) }

					<uo-button 
						slot="target"
						color="transparent"
						size="small"
						@click=${ this.requestCloseDialog }
					>
						<uo-icon id="xmark"></uo-icon>
					</uo-button>
				</uo-tooltip>
				
			</div>
		`;
	}

	/**
	 * Returns the template of the content of the sidebar
	 *
	 * @return {TemplateResult} The template of the content
	 */
	get _templateContent() {
		return html`
			<div class="sidebar-content">
				<uap-template-sidebar-requirements
					.requirements=${ this.requirements }
					?standalone-mode=${ this.standaloneMode }
				></uap-template-sidebar-requirements>
			</div>
		`;
	}

	/**
	 * Returns the template with the button to import the recipe
	 *
	 * @return {TemplateResult|undefined} The template with the button to import the recipe
	 */
	get _templateCTA() {
		// Check if the template is in standalone mode
		if ( this.standaloneMode ) {
			return;
		}

		return html`
			<div class="sidebar-cta">
				<uap-template-button-import-recipe
					template-id=${ this.templateID }
				></uap-template-button-import-recipe>
			</div>
		`;
	}
}
